/**
 * Which object a card shows — spec 012 §13, spec 013 §13.
 *
 * **A pure function over scene kinds, not a model turn.** The object is a *category
 * signal*, and what a lesson makes the learner do is exactly the category; a model would
 * spend a minute to be less consistent than a lookup. With no image vendor in v1,
 * re-deriving this is what "regenerate the art" means.
 *
 * The tint is not decided here. It follows `subject` and is never stored.
 */
/**
 * The seven groups the library is organised into — VIS-13.
 *
 * Here rather than in `packages/ui` beside the drawings, because both readers need it and
 * only one of them may import that package: `bodies.ts` checks every name draws, and the
 * console groups its dropdown. A copy in the console would be a second list of
 * thirty-nine names, drifting from the first the day an object is added.
 */
export const ILLUSTRATION_GROUPS = {
    argument: ['balance', 'funnel', 'stack', 'bridge', 'steps', 'podium'],
    measure: [
        'chart',
        'pie',
        'gauge',
        'magnifier',
        'beaker',
        'hourglass',
        'calendar',
        'clock',
        'stopwatch',
    ],
    money: ['vault', 'wallet', 'receipt', 'calculator', 'ledger'],
    direction: ['compass', 'telescope', 'map', 'flag', 'chess', 'target'],
    people: [
        'gavel',
        'puzzle',
        'team',
        'network',
        'roundtable',
        'mic',
        'iceberg',
        'chat',
    ],
    operations: [
        'gears',
        'pipeline',
        'crate',
        'factory',
        'server',
        'spool',
        'clipboard',
        'tray',
        'basket',
        'folder',
        'bin',
    ],
    momentum: ['lightbulb', 'rocket', 'key', 'trophy', 'briefcase'],
};
export const ILLUSTRATION_GROUP_NAMES = Object.keys(ILLUSTRATION_GROUPS);
/** The heading a grouped picker prints. The key is a lookup word, not a reading one. */
export const illustrationGroupCopy = {
    argument: 'Argument',
    measure: 'Measure',
    money: 'Money',
    direction: 'Direction',
    people: 'People',
    operations: 'Operations',
    momentum: 'Momentum',
};
/**
 * Which group an object belongs to, for a picker printing a heading as the group turns.
 *
 * Inverted once at module load rather than scanned per call: a thirty-nine-option select
 * asks this for every option on every render, and a scan needs a fallback for the object
 * in no group — which reads as a default while being unreachable. The cast is what
 * `illustration.test.ts` proves, filing every object under exactly one group.
 */
const GROUP_OF = Object.fromEntries(ILLUSTRATION_GROUP_NAMES.flatMap((group) => ILLUSTRATION_GROUPS[group].map((object) => [object, group])));
export const illustrationGroupOf = (object) => GROUP_OF[object];
/**
 * What each object is called, and what it says about the lesson — GEN-42.
 *
 * A full `Record`, not a partial one: a fortieth shape is a compile error here rather than
 * a screen that falls back to printing the column value, which is the state this replaced.
 * It lives beside the rule that picks the object so the words and the reasoning move
 * together.
 */
export const illustrationCopy = {
    balance: { label: 'Balance', meaning: 'a tradeoff to weigh' },
    funnel: { label: 'Funnel', meaning: 'many options down to one' },
    stack: { label: 'Stack', meaning: 'ideas in layers' },
    bridge: { label: 'Bridge', meaning: 'a case to cross' },
    steps: { label: 'Steps', meaning: 'an order to follow' },
    podium: { label: 'Podium', meaning: 'something to say out loud' },
    chart: { label: 'Chart', meaning: 'a trend to read' },
    pie: { label: 'Pie', meaning: 'a whole split into shares' },
    gauge: { label: 'Gauge', meaning: 'a reading against a limit' },
    magnifier: { label: 'Magnifier', meaning: 'a number worth looking into' },
    beaker: { label: 'Beaker', meaning: 'a test to run' },
    hourglass: { label: 'Hourglass', meaning: 'something time decides' },
    calendar: { label: 'Calendar', meaning: 'a date to hold to' },
    clock: { label: 'Clock', meaning: 'time running down' },
    stopwatch: { label: 'Stopwatch', meaning: 'how long it takes' },
    vault: { label: 'Vault', meaning: 'money held back' },
    wallet: { label: 'Wallet', meaning: 'what a customer will spend' },
    receipt: { label: 'Receipt', meaning: 'what it actually cost' },
    calculator: { label: 'Calculator', meaning: 'a sum to work out' },
    ledger: { label: 'Ledger', meaning: 'the books to balance' },
    compass: { label: 'Compass', meaning: 'a direction to pick' },
    telescope: { label: 'Telescope', meaning: 'something far off to plan for' },
    map: { label: 'Map', meaning: 'the ground to cover' },
    flag: { label: 'Flag', meaning: 'a goal to reach' },
    chess: { label: 'Chess', meaning: "a rival's next move" },
    target: { label: 'Target', meaning: 'who it is aimed at' },
    gavel: { label: 'Gavel', meaning: 'a call to make' },
    puzzle: { label: 'Puzzle', meaning: 'pieces that must fit' },
    team: { label: 'Team', meaning: 'who does the work' },
    network: { label: 'Network', meaning: 'who knows whom' },
    roundtable: { label: 'Roundtable', meaning: 'a room to win over' },
    mic: { label: 'Mic', meaning: 'a story to tell' },
    iceberg: { label: 'Iceberg', meaning: 'the need under the demand' },
    chat: { label: 'Chat', meaning: 'a request put into words' },
    gears: { label: 'Gears', meaning: 'a process to tune' },
    pipeline: { label: 'Pipeline', meaning: 'work moving through stages' },
    crate: { label: 'Crate', meaning: 'goods to move' },
    factory: { label: 'Factory', meaning: 'making it at scale' },
    server: { label: 'Server', meaning: 'the system underneath' },
    spool: { label: 'Spool', meaning: 'material before it is made' },
    clipboard: { label: 'Clipboard', meaning: 'the facts handed over' },
    tray: { label: 'Tray', meaning: 'work waiting its turn' },
    basket: { label: 'Basket', meaning: 'what the buyer picks up' },
    folder: { label: 'Folder', meaning: 'papers kept together' },
    bin: { label: 'Bin', meaning: 'what you let go' },
    lightbulb: { label: 'Lightbulb', meaning: 'an idea worth testing' },
    rocket: { label: 'Rocket', meaning: 'growth to launch' },
    key: { label: 'Key', meaning: 'what unlocks it' },
    trophy: { label: 'Trophy', meaning: 'a win to defend' },
    briefcase: { label: 'Briefcase', meaning: 'the deal itself' },
};
/**
 * The rule from `012` §13, read as: what does this interaction ask of the learner?
 *
 * `SLIDER` and `ALLOCATE` are the two that hand the learner a quantity to divide, which is
 * a tradeoff. `BUCKET` is `stack` rather than `funnel` because sorting into named groups is
 * layering, where `CHOICE` cuts many options down to one.
 */
const BY_KIND = {
    SLIDER: 'balance',
    ALLOCATE: 'balance',
    CHOICE: 'funnel',
    BUCKET: 'stack',
    RANK: 'steps',
    SPEAK: 'podium',
    CASE: 'bridge',
    CONCEPT: 'stack',
};
/**
 * The dominant interaction decides. Ties go to the earliest scene, because a lesson opens
 * with what it is about.
 *
 * Narrative kinds (`HOOK`, `RECAP`, `REWARD`, `ARTICLE`) carry no object: every lesson
 * has them, so counting them would make every card a `stack`.
 */
export function objectFor(kinds) {
    const counts = new Map();
    let best;
    let bestCount = 0;
    for (const kind of kinds) {
        const object = BY_KIND[kind];
        if (object === undefined)
            continue;
        const next = (counts.get(object) ?? 0) + 1;
        counts.set(object, next);
        // Strictly greater, so a tie leaves the earlier scene's object in place.
        if (next > bestCount) {
            best = object;
            bestCount = next;
        }
    }
    return best;
}
/**
 * Which object a subject's hook hero plays against — ANIM-16.
 *
 * `BY_KIND` above answers *what does this lesson ask of the learner*; this answers *what
 * is this lesson about*. A HOOK has no interaction to key off, so its hero reaches for
 * the subject instead.
 *
 * Twenty-three onto six, so objects repeat, and that is the point: `FINANCE` and `DECISIONS`
 * both weigh a tradeoff on the `balance`, and what separates them is what the mascot does
 * on it. The act carries the meaning; the prop is the stage it is carried on. Still the
 * `argument` six after VIS-13: `docs/decisions/illustration-object-groups.md`.
 */
export const objectForSubject = {
    MARKETING: 'funnel',
    FINANCE: 'balance',
    STRATEGY: 'steps',
    LEADERSHIP: 'podium',
    SALES: 'bridge',
    NEGOTIATION: 'bridge',
    ENTREPRENEURSHIP: 'stack',
    PUBLIC_SPEAKING: 'podium',
    COMMUNICATION: 'podium',
    OPERATIONS: 'stack',
    DECISIONS: 'balance',
    BUSINESS_FUNDAMENTALS: 'balance',
    TEAM_MANAGEMENT: 'podium',
    HIRING: 'bridge',
    ORGANIZATIONAL_BEHAVIOR: 'bridge',
    PRODUCT_MANAGEMENT: 'funnel',
    AI: 'steps',
    INNOVATION: 'stack',
    PROJECT_MANAGEMENT: 'stack',
    BUSINESS_ANALYTICS: 'funnel',
    CAREER: 'steps',
    PERSONAL_FINANCE: 'balance',
    ECONOMICS: 'balance',
};
/**
 * What a narrative scene stands in the space its copy leaves — VIS-12.
 *
 * `objectFor` gives the narrative kinds nothing by design, so before this the player
 * floored every one of them to the same drawing. A hook has no interaction to key
 * off, but it does have a subject.
 *
 * The fallback is `familyOf`'s: a lesson owned entirely by subjectless paths is
 * tinted `FINANCE`, so its art is the one `FINANCE` reaches for rather than a
 * second decision that could disagree with the tint.
 */
export function narrativeObject(subject) {
    return objectForSubject[subject ?? 'FINANCE'];
}
//# sourceMappingURL=illustration.js.map